// File Location: src/pages/dashboard/DoctorDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  History,
  User,
  Stethoscope,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  RefreshCw,
  Eye,
  Play,
  MapPin,
  Phone,
  Building,
  CreditCard,
  Activity,
  TrendingUp,
  BarChart3,
  Timer,
  Pause,
  FolderOpen
} from 'lucide-react';
import {
  getDoctorApplications,
  peekDoctorApplicationsCache,
  getDoctorReviewedApplications,
  getDoctorStats,
  startApplicationReview,
  resumeApplicationReview,
  getApplicationsOnHold
} from '../../services/doctorService';
import { useAuth } from '../../context/AuthContext';
// auth not needed here
import { SignOutIconButton } from '../../component/common/SignOutButton';
import NotificationBell from '../../components/common/NotificationBell';
import RoleSwitcher from '../../components/common/RoleSwitcher';
import samhealthLogo from '../../assets/samhealth.png';
import UniversalSearch from '../../components/common/UniversalSearch';

const DoctorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData: user } = useAuth();

  const [activeSection, setActiveSection] = useState('dashboard');
  const [applications, setApplications] = useState([]);
  const [allApplications, setAllApplications] = useState([]);
  const [onHoldApplications, setOnHoldApplications] = useState([]);
  const [reviewedApplications, setReviewedApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [stats, setStats] = useState({
    pendingReview: 0,
    reviewedToday: 0,
    totalReviewed: 0,
    averageReviewTime: 0,
    emergencyPending: 0,
    approvalRate: 0
  });

  // Filters and pagination
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    emergency: '',
    treatmentType: '',
    searchTerm: '',
    dateFrom: '',
    dateTo: '',
    insuranceCompany: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [appsStageCounts, setAppsStageCounts] = useState({});
  const [allAppsStageCounts, setAllAppsStageCounts] = useState({});
  const [appsTotal, setAppsTotal] = useState(0);
  const [allAppsTotal, setAllAppsTotal] = useState(0);
  // Sub-tabs state for bifurcation within sections
  const [activeAppsTab, setActiveAppsTab] = useState('new'); // 'all' | 'new' | 'kyp_pending' | 'discharge' | 'document_scrutiny'
  const [activeAllAppsTab, setActiveAllAppsTab] = useState('new'); // 'all' | 'new' | 'kyp_pending' | 'discharge' | 'document_scrutiny'

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard Overview', icon: LayoutDashboard },
    { id: 'applications', label: 'Pending Review', icon: FileText },
    { id: 'on-hold', label: 'Applications on Hold', icon: AlertTriangle },
    { id: 'all-applications', label: 'All Applications', icon: BarChart3 },
    { id: 'reviewed', label: 'Reviewed by Me', icon: History },
    { id: 'documents', label: 'Documents', icon: FolderOpen },
    { id: 'profile', label: 'My Profile', icon: User }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'submitted', label: 'Submitted', color: 'bg-blue-100 text-blue-800' },
    { value: 'under_medical_review', label: 'Under Review', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'medical_review_paused', label: 'Paused', color: 'bg-blue-100 text-blue-800' },
    { value: 'medical_approved', label: 'Approved', color: 'bg-green-100 text-green-800' },
    { value: 'medical_rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    { value: 'approved_for_process', label: 'Approved for Process', color: 'bg-teal-100 text-teal-800' },
    { value: 'policy_approved', label: 'Policy Approved', color: 'bg-green-100 text-green-800' },
    { value: 'claim_processed', label: 'Claim Processed', color: 'bg-purple-100 text-purple-800' },
    { value: 'dispute_pending', label: 'Dispute Pending', color: 'bg-orange-100 text-orange-800' }
  ];

  const priorityOptions = [
    { value: '', label: 'All Priority' },
    { value: 'normal', label: 'Normal', color: 'bg-gray-100 text-gray-800' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
  ];

  const treatmentTypes = [
    { value: '', label: 'All Types' },
    { value: 'medical', label: 'Medical' },
    { value: 'surgical', label: 'Surgical' }
  ];

  const insuranceCompanies = [
    'HDFC ERGO', 'ICICI Lombard', 'Bajaj Allianz', 'Star Health', 'Care Health',
    'Niva Bupa', 'Aditya Birla', 'Future Generali', 'Cholamandalam', 'Oriental Insurance'
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadDashboardData();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeSection === 'applications') {
      loadPendingApplications();
    } else if (activeSection === 'all-applications') {
      loadAllApplications();
    } else if (activeSection === 'on-hold') {
      loadApplicationsOnHold();
    } else if (activeSection === 'reviewed') {
      loadReviewedApplications();
    }
  }, [activeSection, filters, currentPage, activeAppsTab, activeAllAppsTab]);

  // Refresh data when user returns to the tab/window
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleFocus = () => {
      loadStats();
      refreshCurrentData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [activeSection]);

  // Refresh data when location changes (e.g., coming back from review)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (location.pathname === '/doctor/dashboard') {
      loadStats();
      refreshCurrentData();
    }
  }, [location.pathname]);

  const loadStats = async () => {
    try {
      const result = await getDoctorStats(user.uid);
      if (result.success) {
        setStats(result.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Restored: dashboard aggregate loader (stats + recent applications snapshot)
  const loadDashboardData = async () => {
    try {
      await loadStats();
      const applicationsResult = await getDoctorApplications({}, 1, 5);
      if (applicationsResult.success) {
        setApplications(applicationsResult.applications);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  // Background prefetch for adjacent tabs to eliminate wait on tab switch
  const prefetchDoctorApplicationsTabs = async (baseFilters, tabs, getAll = false) => {
    try {
      const uniqueTabs = Array.from(new Set(tabs.filter(Boolean)));
      await Promise.all(
        uniqueTabs.map((tab) => {
          const stageFilter = tab !== 'all' ? { _stage: tab } : {};
          return getDoctorApplications({ ...baseFilters, ...stageFilter, _doctorId: user?.uid }, 1, pageSize, getAll).catch(() => null);
        })
      );
    } catch (_) {}
  };

  const loadPendingApplications = async () => {
    const stageFilter = activeAppsTab && activeAppsTab !== 'all' ? { _stage: activeAppsTab } : {};
    const cached = peekDoctorApplicationsCache({ ...filters, ...stageFilter, _doctorId: user?.uid }, currentPage, pageSize, false);
    if (cached && Array.isArray(cached.applications) && (cached.requestedStage || 'all') === (activeAppsTab || 'all') && cached.requestedGetAll === false) {
      const listWarm = activeAppsTab === 'new' ? sortLatestFirst(cached.applications) : cached.applications;
      setApplications(listWarm);
      setTotalPages(Math.ceil((cached.total || listWarm.length) / pageSize));
      setAppsStageCounts(cached.stageCounts || {});
      setAppsTotal(cached.total || listWarm.length);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      // Always pass doctor id so badge counts are doctor-specific
      const result = await getDoctorApplications({ ...filters, ...stageFilter, _doctorId: user?.uid }, currentPage, pageSize);
      if (result.success) {
        // If New tab, ensure latest first
        const list = activeAppsTab === 'new' ? sortLatestFirst(result.applications) : result.applications;
        setApplications(list);
        setTotalPages(Math.ceil(result.total / pageSize));
        setAppsStageCounts(result.stageCounts || {});
        setAppsTotal(result.total || list.length);
        // Prefetch nearby tabs for smoother switching
        const tabsToPrefetch = ['new', 'kyp_pending', 'discharge_pending', 'scrutiny_pending', 'query_raised']
          .filter((t) => t !== (activeAppsTab || 'new'));
        // Use same base filters (first page) for warm cache
        const baseFilters = { ...filters };
        Promise.resolve().then(() => prefetchDoctorApplicationsTabs(baseFilters, tabsToPrefetch, false));
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllApplications = async () => {
    try {
      // Create filters for all applications (remove status filter to get all)
      const allFilters = { ...filters };
      delete allFilters.status; // Remove status filter to get applications in all states
      const stageFilter = activeAllAppsTab && activeAllAppsTab !== 'all' ? { _stage: activeAllAppsTab } : {};
      const cached = peekDoctorApplicationsCache({ ...allFilters, ...stageFilter, _doctorId: user?.uid }, currentPage, pageSize, true);
      if (cached && Array.isArray(cached.applications) && (cached.requestedStage || 'all') === (activeAllAppsTab || 'all') && cached.requestedGetAll === true) {
        const listWarm = sortLatestFirst(cached.applications || []);
        setAllApplications(listWarm);
        setTotalPages(Math.ceil((cached.total || listWarm.length) / pageSize));
        setAllAppsStageCounts(cached.stageCounts || {});
        setAllAppsTotal(cached.total || listWarm.length);
        setLoading(false);
      } else {
        setLoading(true);
      }
      const result = await getDoctorApplications({ ...allFilters, ...stageFilter, _doctorId: user?.uid }, currentPage, pageSize, true);
      if (result.success) {
        const list = sortLatestFirst(result.applications || []);
        setAllApplications(list);
        setTotalPages(Math.ceil(result.total / pageSize));
        setAllAppsStageCounts(result.stageCounts || {});
        setAllAppsTotal(result.total || list.length);
        // Prefetch nearby tabs for All Applications section as well
        const tabsToPrefetch = ['all', 'new', 'kyp_pending', 'discharge_pending', 'scrutiny_pending']
          .filter((t) => t !== (activeAllAppsTab || 'new'));
        const baseFilters = { ...allFilters };
        Promise.resolve().then(() => prefetchDoctorApplicationsTabs(baseFilters, tabsToPrefetch, true));
      }
    } catch (error) {
      console.error('Error loading all applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReviewedApplications = async () => {
    setLoading(true);
    try {
      const result = await getDoctorReviewedApplications(user.uid, filters, currentPage, pageSize);
      if (result.success) {
        setReviewedApplications(result.applications);
        setTotalPages(Math.ceil(result.total / pageSize));
      }
    } catch (error) {
      console.error('Error loading reviewed applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApplicationsOnHold = async () => {
    setLoading(true);
    try {
      const result = await getApplicationsOnHold(filters, currentPage, pageSize);
      if (result.success) {
        setOnHoldApplications(result.applications);
        setTotalPages(Math.ceil(result.total / pageSize));
      }
    } catch (error) {
      console.error('Error loading applications on hold:', error);
    } finally {
      setLoading(false);
    }
  };

  // Derive app stage for bifurcation
  // Removed unused deriveStage helper

  const sortLatestFirst = (list) => {
    return [...list].sort((a, b) => {
      const da = a?.submittedAt?.toDate ? a.submittedAt.toDate() : (a?.submittedAt ? new Date(a.submittedAt) : new Date(0));
      const db = b?.submittedAt?.toDate ? b.submittedAt.toDate() : (b?.submittedAt ? new Date(b.submittedAt) : new Date(0));
      return db - da;
    });
  };

  // Doctor-specific bucketing logic matching deriveStageForDoctor from doctorService
  const doctorBucketKeysForApp = React.useCallback((application) => {
    const keys = [];
    const w = application?.workflow || {};
    const mv = w.medicalVerification || w.medicalReview || {};
    const pv = w.policyVerification || {};
    const ds = w.dischargeSettlement || {};
    const dispatch = w.dispatch || w.dispatches || {};
    const disb = w.disbursal || {};
    const recovery = w.recovery || {};

    // KYP approval/acknowledgement status
    const kypApprovalStatus = mv?.kypApproval?.status || '';
    const kypAccepted = kypApprovalStatus === 'approved';
    const kypChallenged = mv?.kypHospitalAcknowledgement?.status === 'challenged';
    const kypRejected = kypApprovalStatus === 'rejected' || mv?.kypHospitalAcknowledgement?.status === 'rejected';

    // FAL approval/acknowledgement status
    const anyFalChallenged = Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'challenged');
    const allFalAccepted = ds?.hospitalAcknowledgement?.status === 'accepted' || (Array.isArray(w?.sanctions) && w.sanctions.every(s => s?.hospitalAcknowledgement?.status === 'accepted'));
    const anyFalRejected = Array.isArray(w?.sanctions) && w.sanctions.some(s => s?.hospitalAcknowledgement?.status === 'rejected');
    const falAck = ds?.hospitalAcknowledgement?.status;
    const falGenerated = !!ds?.finalApprovalLetterUrl;

    // Discharge / document flags
    const hasUploadedDischargeDocs = Array.isArray(ds?.uploadedDocuments) ? ds.uploadedDocuments.length > 0 : (Array.isArray(ds?.documents) && ds.documents.length > 0);
    const dischargeStarted = application?.dischargeInitiated || ds?.status;
    const hasDispatch = !!dispatch?.status || (Array.isArray(dispatch) && dispatch.length > 0);

    // Query status heuristics
    const unresolvedQueryCount = application?.queryStatus?.queryCount || 0;
    const hasUnresolvedQueries = unresolvedQueryCount > 0 || application?.status === 'dispute_pending';
    const queryReplyReceived = !!(application?.queryReplies && application.queryReplies.hasHospitalReply);
    const diagnosisApprovalPending = !!(application?.queryReplies && application.queryReplies.hasAcceptedReply && (mv?.status === 'pending'));

    // Rejection grouping
    const medicalRejected = application?.status === 'medical_rejected' || application?.status === 'rejected';
    const falRejected = anyFalRejected || falAck === 'rejected' || application?.status === 'fal_rejected';
    const isRejected = medicalRejected || kypRejected || falRejected;

    // Context phase helpers for categorizing query reply buckets
    const inKypPhase = !kypAccepted && !kypChallenged && !kypRejected;
    const inDischargePhase = (kypAccepted && (dischargeStarted || hasUploadedDischargeDocs) && !allFalAccepted && !falRejected);
    const inScrutinyPhase = allFalAccepted && !disb?.status && !recovery?.pendingTasks;

    // 1. Query Raised
    if (hasUnresolvedQueries) keys.push('query_raised');

    // 2. Rejected Challenge
    if (isRejected && (kypChallenged || anyFalChallenged)) keys.push('rejected_challenge');

    // 3. Rejected
    if (isRejected) keys.push('rejected');

    // 4. Diagnosis Approval Pending
    if (diagnosisApprovalPending) keys.push('diagnosis_approval_pending');

    // 5/6/7. Query reply received contextual buckets
    if (queryReplyReceived) {
      if (inKypPhase) keys.push('kyp_query_reply_received');
      if (inDischargePhase) keys.push('discharge_query_reply_received');
      if (inScrutinyPhase) keys.push('scrutiny_query_reply_received');
    }

    // 8. KYP Challenged
    if (kypChallenged) keys.push('kyp_challenged');

    // 9. FAL Challenged
    if (anyFalChallenged || falAck === 'challenged') keys.push('fal_challenged');

    // 10. KYP Pending
    const policyReady = !!(pv?.status === 'completed' || pv?.coverageBreakdown || pv?.policyDetails);
    const kypGeneratedOrApproved = (mv?.kypGenerated || kypApprovalStatus === 'approved' || kypAccepted);
    const blockedByHoldOrRejection = isRejected || application?.status === 'dispute_pending';
    if (policyReady && !kypGeneratedOrApproved && !blockedByHoldOrRejection) keys.push('kyp_pending');

    // 11. KYP Approved
    if (kypAccepted && !dischargeStarted && !hasUploadedDischargeDocs && !falGenerated) keys.push('kyp_approved');

    // 12. Discharge Pending
    if ((dischargeStarted || hasUploadedDischargeDocs) && !falGenerated && !allFalAccepted) keys.push('discharge_pending');

    // 13. Discharge Approved
    if (falGenerated && !allFalAccepted) keys.push('discharge_approved');

    // 14. FAL Approved
    if (allFalAccepted && !hasDispatch && !disb?.status) keys.push('fal_approved');

    // 15. Scrutiny Pending
    if (allFalAccepted && !hasDispatch && !disb?.status) keys.push('scrutiny_pending');

    // 16. Scrutiny Approved
    const scrutinyApprovedSignal = ds?.scrutiny?.status === 'approved' || hasDispatch;
    if (allFalAccepted && scrutinyApprovedSignal && !disb?.status) keys.push('scrutiny_approved');

    // 17. New - Applications where initial diagnosis approval has NOT been done
    // This is the key bucket: approvedForProcess indicates initial diagnosis approval is complete
    const isNew = !mv?.approvedForProcess && !mv?.approvedForProcessBy && application?.status === 'submitted';
    if (isNew) keys.push('new');

    return keys;
  }, []);

  // Choose a single canonical bucket for counting
  const doctorPrimaryBucketForApp = React.useCallback((application) => {
    const keys = doctorBucketKeysForApp(application);
    const priority = [
      'scrutiny_approved',
      'scrutiny_pending',
      'fal_approved',
      'discharge_approved',
      'discharge_pending',
      'kyp_approved',
      'kyp_pending',
      'fal_challenged',
      'kyp_challenged',
      'scrutiny_query_reply_received',
      'discharge_query_reply_received',
      'kyp_query_reply_received',
      'diagnosis_approval_pending',
      'rejected',
      'rejected_challenge',
      'query_raised',
      'new',
    ];
    for (const p of priority) if (keys.includes(p)) return p;
    return 'new';
  }, [doctorBucketKeysForApp]);

  const buildDoctorStageCounts = React.useCallback((applications) => {
    const counts = {
      all: 0,
      new: 0,
      query_raised: 0,
      kyp_query_reply_received: 0,
      diagnosis_approval_pending: 0,
      kyp_pending: 0,
      kyp_challenged: 0,
      kyp_approved: 0,
      discharge_pending: 0,
      discharge_query_reply_received: 0,
      discharge_approved: 0,
      fal_challenged: 0,
      fal_approved: 0,
      scrutiny_pending: 0,
      scrutiny_query_reply_received: 0,
      scrutiny_approved: 0,
      rejected_challenge: 0,
      rejected: 0,
    };
    counts.all = applications.length;
    applications.forEach((app) => {
      const keys = doctorBucketKeysForApp(app);
      keys.forEach((key) => {
        if (counts.hasOwnProperty(key)) {
          counts[key] += 1;
        }
      });
    });
    return counts;
  }, [doctorBucketKeysForApp]);

  const matchesDoctorBucket = React.useCallback((application, bucketKey) => {
    if (bucketKey === 'all') return true;
    const keys = doctorBucketKeysForApp(application);
    return keys.includes(bucketKey);
  }, [doctorBucketKeysForApp]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      priority: '',
      emergency: '',
      treatmentType: '',
      searchTerm: '',
      dateFrom: '',
      dateTo: '',
      insuranceCompany: ''
    });
    setCurrentPage(1);
  };

  const handleStartReview = async (applicationId) => {
    setActionLoading(prev => ({ ...prev, [applicationId]: true }));
    try {
      const result = await startApplicationReview(applicationId, user.uid);
      if (result.success) {
        navigate(`/doctor/application-review/${applicationId}`);
      } else {
        alert('Error starting review: ' + result.error);
      }
    } catch (error) {
      console.error('Error starting review:', error);
      alert('Failed to start review');
    } finally {
      setActionLoading(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  const handleViewApplication = (applicationId) => {
    navigate(`/doctor/application-review/${applicationId}`);
  };

  const handleResumeReview = async (applicationId) => {
    setActionLoading(prev => ({ ...prev, [applicationId]: true }));
    try {
      const result = await resumeApplicationReview(applicationId, user.uid);
      if (result.success) {
        navigate(`/doctor/application-review/${applicationId}`);
      } else {
        alert('Error resuming review: ' + result.error);
      }
    } catch (error) {
      console.error('Error resuming review:', error);
      alert('Failed to resume review');
    } finally {
      setActionLoading(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  // Function to refresh current data
  const refreshCurrentData = () => {
    if (activeSection === 'applications') {
      loadPendingApplications();
    } else if (activeSection === 'all-applications') {
      loadAllApplications();
    } else if (activeSection === 'on-hold') {
      loadApplicationsOnHold();
    } else if (activeSection === 'reviewed') {
      loadReviewedApplications();
    } else if (activeSection === 'dashboard') {
      loadDashboardData();
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(s => s.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const priorityOption = priorityOptions.find(p => p.value === priority);
    return priorityOption ? priorityOption.color : 'bg-gray-100 text-gray-800';
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Doctor Dashboard</h1>
        <p className="text-gray-600">Medical review and application management</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase">Pending Review</p>
              <p className="text-2xl font-bold text-gray-800">{stats.pendingReview}</p>
            </div>
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase">Today's Reviews</p>
              <p className="text-2xl font-bold text-gray-800">{stats.reviewedToday}</p>
            </div>
            <div className="bg-blue-100 p-2 rounded-lg">
              <Activity className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase">Total Reviewed</p>
              <p className="text-2xl font-bold text-gray-800">{stats.totalReviewed}</p>
            </div>
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase">Avg Review Time</p>
              <p className="text-2xl font-bold text-gray-800">{stats.averageReviewTime}min</p>
            </div>
            <div className="bg-purple-100 p-2 rounded-lg">
              <Timer className="h-5 w-5 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase">Emergency Cases</p>
              <p className="text-2xl font-bold text-gray-800">{stats.emergencyPending}</p>
            </div>
            <div className="bg-red-100 p-2 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase">Approval Rate</p>
              <p className="text-2xl font-bold text-gray-800">{stats.approvalRate}%</p>
            </div>
            <div className="bg-teal-100 p-2 rounded-lg">
              <TrendingUp className="h-5 w-5 text-teal-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Overview Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Recent Applications</h3>
            <button
              onClick={() => setActiveSection('applications')}
              className="btn-primary text-sm"
            >
              View All Applications
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Diagnosis</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hospital</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lifecycle</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.slice(0, 5).map((application) => (
                <tr key={application.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                        <User className="h-4 w-4 text-teal-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{application.patient?.name}</div>
                        <div className="text-sm text-gray-500">{application.patient?.age}Y, {application.patient?.gender}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{application.medical?.diagnosis}</div>
                    <div className="text-sm text-gray-500">{application.medical?.treatmentType}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-normal break-words text-sm text-gray-900">
                    {application.hospitalName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(application.priority)}`}>
                        {application.priority?.charAt(0).toUpperCase() + application.priority?.slice(1)}
                      </span>
                      {application.emergency && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          Emergency
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(application.submittedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderLifecycleChips(application)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      {application.workflow?.medicalReview?.status === 'paused' &&
                        application.workflow?.medicalReview?.reviewerId === user.uid ? (
                        // Resume button for paused applications by current doctor
                        <button
                          onClick={() => handleResumeReview(application.applicationId)}
                          disabled={actionLoading[application.applicationId]}
                          className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Resume Paused Review"
                        >
                          {actionLoading[application.applicationId] ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              Resuming...
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4 mr-1" />
                              Resume
                            </>
                          )}
                        </button>
                      ) : application.workflow?.medicalReview?.status === 'in_progress' &&
                        application.workflow?.medicalReview?.reviewerId === user.uid ? (
                        // Continue button for applications being actively reviewed by current doctor
                        <button
                          onClick={() => handleViewApplication(application.applicationId)}
                          className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
                          title="Continue Review"
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Continue
                        </button>
                      ) : (application.workflow?.medicalReview?.status === 'in_progress' ||
                        application.workflow?.medicalReview?.status === 'paused') ? (
                        // View only for applications being reviewed/paused by others
                        <button
                          onClick={() => handleViewApplication(application.applicationId)}
                          className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                          title="View (Under Review by Another Doctor)"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </button>
                      ) : (
                        // Start button for new applications
                        <button
                          onClick={() => handleStartReview(application.applicationId)}
                          disabled={actionLoading[application.applicationId]}
                          className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Start Review"
                        >
                          {actionLoading[application.applicationId] ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              Starting...
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4 mr-1" />
                              Start
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );


  // Helper: render lifecycle status chips in a single dedicated column
  // Shows: Processing → KYP generated → KYP accepted/challenged/rejected → Discharge started →
  // FAL generated → FAL accepted/challenged/rejected → Dispatch → Disbursed
  function renderLifecycleChips(application) {
    const w = application?.workflow || {};
    const mv = w.medicalVerification || {};
    const mr = w.medicalReview || {};
    const ds = w.dischargeSettlement || {};
    const disb = w.disbursal || {};
    const dispatch = w.dispatch || w.dispatches || {};

    const chips = [];

    // Processing
    const isInProgress = mr?.status === 'in_progress' || application?.status === 'under_medical_review' || application?.status === 'submitted' || application?.status === 'medical_review_pending';
    if (isInProgress) {
      chips.push({ label: 'Processing', cls: 'bg-blue-100 text-blue-800 border-blue-200' });
    }

    // KYP generated
    if (mv?.kypGenerated) {
      chips.push({ label: 'KYP Generated', cls: 'bg-green-100 text-green-800 border-green-200' });
    }

    // KYP hospital acknowledgement
    const kypAck = mv?.kypHospitalAcknowledgement?.status;
    if (kypAck === 'accepted') {
      chips.push({ label: 'KYP Accepted', cls: 'bg-green-100 text-green-800 border-green-200' });
    } else if (kypAck === 'challenged') {
      chips.push({ label: 'KYP Challenged', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' });
    } else if (kypAck === 'rejected') {
      chips.push({ label: 'KYP Rejected', cls: 'bg-red-100 text-red-800 border-red-200' });
    }

    // Discharge process
    const dischargeStarted = application?.dischargeInitiated || ds?.status;
    if (dischargeStarted) {
      chips.push({ label: 'Discharge Started', cls: 'bg-purple-100 text-purple-800 border-purple-200' });
    }

    // FAL (Final Approval Letter) generated
    if (ds?.finalApprovalLetterUrl) {
      chips.push({ label: 'FAL Generated', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' });
    }

    // FAL acknowledgement (if tracked)
    const falAck = ds?.hospitalAcknowledgement?.status;
    // Fallback via sanctions (if present)
    const sanctions = w?.sanctions;
    const anyFalChallenged = Array.isArray(sanctions) && sanctions.some(s => s?.hospitalAcknowledgement?.status === 'challenged');
    const anyFalRejected = Array.isArray(sanctions) && sanctions.some(s => s?.hospitalAcknowledgement?.status === 'rejected');
    const allFalAccepted = Array.isArray(sanctions) && sanctions.length > 0 && sanctions.every(s => s?.hospitalAcknowledgement?.status === 'accepted');
    if (falAck === 'accepted' || allFalAccepted) {
      chips.push({ label: 'FAL Accepted', cls: 'bg-green-100 text-green-800 border-green-200' });
    } else if (falAck === 'challenged' || anyFalChallenged) {
      chips.push({ label: 'FAL Challenged', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' });
    } else if (falAck === 'rejected' || anyFalRejected) {
      chips.push({ label: 'FAL Rejected', cls: 'bg-red-100 text-red-800 border-red-200' });
    }

    // Dispatch process (best-effort)
    if (dispatch?.status) {
      chips.push({ label: `Dispatch: ${String(dispatch.status).replace(/_/g, ' ')}`, cls: 'bg-sky-100 text-sky-800 border-sky-200' });
    } else if (ds?.finalApprovalLetterUrl && !disb?.status) {
      chips.push({ label: 'Dispatch Pending', cls: 'bg-sky-50 text-sky-700 border-sky-200' });
    }

    // Disbursed
    if (disb?.status === 'disbursed') {
      chips.push({ label: 'Disbursed', cls: 'bg-teal-100 text-teal-800 border-teal-200' });
    } else if (disb?.status) {
      chips.push({ label: `Disbursal: ${String(disb.status).replace(/_/g, ' ')}`, cls: 'bg-teal-50 text-teal-800 border-teal-200' });
    }

    if (chips.length === 0) {
      chips.push({ label: 'Processing', cls: 'bg-blue-50 text-blue-700 border-blue-200' });
    }

    return (
      <div className="flex flex-wrap items-center gap-1">
        {chips.map((c, idx) => (
          <span key={idx} className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${c.cls}`}>
            {c.label}
          </span>
        ))}
      </div>
    );
  }
  const renderApplications = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pending Applications</h1>
          <p className="text-gray-600">Applications awaiting medical review</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={loadPendingApplications} className="btn-outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Bifurcation Tabs */}
      {(() => {
        const counts = appsStageCounts || {};
        const totalCount = appsTotal || 0;
        const tabBtn = (id, label, count) => (
          <button
            key={id}
            onClick={() => { setLoading(true); setActiveAppsTab(id); setCurrentPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${activeAppsTab === id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
          >
            {label}
            <span className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs rounded-full ${activeAppsTab === id ? 'bg-white text-teal-700' : 'bg-gray-100 text-gray-700'}`}>
              {count}
            </span>
          </button>
        );
        return (
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="flex flex-wrap gap-2">
              {/* Pending Applications - Required Buckets Only (ordered) */}
              {tabBtn('all', 'All', counts.all ?? totalCount)}
              {tabBtn('new', 'New Applications', counts['new'] || 0)}
              {tabBtn('query_raised', 'Query Raised', counts['query_raised'] || 0)}
              {tabBtn('kyp_query_reply_received', 'KYP Query Reply Received', counts['kyp_query_reply_received'] || 0)}
              {tabBtn('diagnosis_approval_pending', 'Diagnosis Approval Pending', counts['diagnosis_approval_pending'] || 0)}
              {tabBtn('kyp_pending', 'KYP Pending', counts['kyp_pending'] || 0)}
              {tabBtn('kyp_challenged', 'KYP Challenged', counts['kyp_challenged'] || 0)}
              {tabBtn('kyp_approved', 'KYP Approved', counts['kyp_approved'] || 0)}
              {tabBtn('discharge_pending', 'Discharge Pending', counts['discharge_pending'] || 0)}
              {tabBtn('discharge_query_reply_received', 'Discharge Query Reply Received', counts['discharge_query_reply_received'] || 0)}
              {tabBtn('discharge_approved', 'Discharge Approved', counts['discharge_approved'] || 0)}
              {tabBtn('fal_challenged', 'FAL Challenged', counts['fal_challenged'] || 0)}
              {tabBtn('fal_approved', 'FAL Approved', counts['fal_approved'] || 0)}
              {tabBtn('scrutiny_pending', 'Scrutiny Pending', counts['scrutiny_pending'] || 0)}
              {tabBtn('scrutiny_query_reply_received', 'Scrutiny Query Reply Received', counts['scrutiny_query_reply_received'] || 0)}
              {tabBtn('scrutiny_approved', 'Scrutiny Approved', counts['scrutiny_approved'] || 0)}
              {tabBtn('rejected_challenge', 'Rejected Challenged', counts['rejected_challenge'] || 0)}
              {tabBtn('rejected', 'Rejected', counts['rejected'] || 0)}
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-4">
          <div>
            <input
              type="text"
              placeholder="Search applications..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input-field"
            >
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="input-field"
            >
              {priorityOptions.map((priority) => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.emergency}
              onChange={(e) => handleFilterChange('emergency', e.target.value)}
              className="input-field"
            >
              <option value="">All Cases</option>
              <option value="true">Emergency Only</option>
              <option value="false">Non-Emergency</option>
            </select>
          </div>

          <div>
            <select
              value={filters.treatmentType}
              onChange={(e) => handleFilterChange('treatmentType', e.target.value)}
              className="input-field"
            >
              {treatmentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.insuranceCompany}
              onChange={(e) => handleFilterChange('insuranceCompany', e.target.value)}
              className="input-field"
            >
              <option value="">All Companies</option>
              {insuranceCompanies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button onClick={resetFilters} className="btn-outline text-sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset Filters
          </button>
          <div className="text-sm text-gray-600">
            {appsTotal} application(s) found
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-teal-500" />
            <span className="ml-2 text-gray-600">Loading applications...</span>
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Found</h3>
            <p className="text-gray-600">No applications match your current filters.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Application ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medical Information</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hospital & Doctor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Policy & Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lifecycle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-normal break-words">
                        <div className="text-sm font-medium text-gray-900">{application.applicationId}</div>
                        <div className="text-sm text-gray-500">
                          {application.workflow?.medicalReview?.status === 'in_progress' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                              <Timer className="h-3 w-3 mr-1" />
                              In Review
                            </span>
                          )}
                          {application.workflow?.medicalReview?.status === 'paused' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                              <Pause className="h-3 w-3 mr-1" />
                              Paused
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                            <User className="h-4 w-4 text-teal-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{application.patient?.name}</div>
                            <div className="text-sm text-gray-500">
                              {application.patient?.age}Y, {application.patient?.gender}
                            </div>
                            <div className="text-xs text-gray-400">
                              <Phone className="h-3 w-3 inline mr-1" />
                              {application.patient?.phone}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">{application.medical?.diagnosis}</div>
                          <div className="text-gray-500">
                            {application.medical?.treatmentType?.charAt(0).toUpperCase() +
                              application.medical?.treatmentType?.slice(1)} Treatment
                          </div>
                          <div className="text-xs text-gray-400">
                            Admission: {application.medical?.admissionDate}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center whitespace-normal break-words">
                            <Building className="h-3 w-3 mr-1 text-gray-400" />
                            {application.hospitalName}
                          </div>
                          <div className="text-gray-500">
                            Dr. {application.medical?.doctor?.name}
                          </div>
                          <div className="text-xs text-gray-400">
                            {application.medical?.doctor?.designation}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center">
                            <CreditCard className="h-3 w-3 mr-1 text-gray-400" />
                            {application.policy?.insuranceCompany}
                          </div>
                          <div className="text-gray-500 font-mono text-xs">
                            {application.policy?.number}
                          </div>
                          <div className="text-sm font-medium text-teal-600">
                            {formatCurrency(application.medical?.estimatedCost)}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(application.priority)}`}>
                            {application.priority?.charAt(0).toUpperCase() + application.priority?.slice(1)}
                          </span>
                          {application.emergency && (
                            <div>
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                Emergency
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(application.submittedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderLifecycleChips(application)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center justify-center">
                          {application.workflow?.medicalReview?.status === 'paused' &&
                            application.workflow?.medicalReview?.reviewerId === user.uid ? (
                            // Resume button for paused applications by current doctor
                            <button
                              onClick={() => handleResumeReview(application.applicationId)}
                              disabled={actionLoading[application.applicationId]}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Resume Paused Review"
                            >
                              {actionLoading[application.applicationId] ? (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  Resuming...
                                </>
                              ) : (
                                <>
                                  <Play className="h-4 w-4 mr-2" />
                                  Resume
                                </>
                              )}
                            </button>
                          ) : application.workflow?.medicalReview?.status === 'in_progress' &&
                            application.workflow?.medicalReview?.reviewerId === user.uid ? (
                            // Continue button for applications being actively reviewed by current doctor
                            <button
                              onClick={() => handleViewApplication(application.applicationId)}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
                              title="Continue Review"
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Continue
                            </button>
                          ) : (application.workflow?.medicalReview?.status === 'in_progress' ||
                            application.workflow?.medicalReview?.status === 'paused') ? (
                            // View only for applications being reviewed/paused by others
                            <button
                              onClick={() => handleViewApplication(application.applicationId)}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              title="View (Under Review by Another Doctor)"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Only
                            </button>
                          ) : (
                            // Start button for new applications
                            <button
                              onClick={() => handleStartReview(application.applicationId)}
                              disabled={actionLoading[application.applicationId]}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Start Review"
                            >
                              {actionLoading[application.applicationId] ? (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  Starting...
                                </>
                              ) : (
                                <>
                                  <Play className="h-4 w-4 mr-2" />
                                  Start
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="btn-outline text-sm"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="btn-outline text-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderAllApplications = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">All Applications</h1>
          <p className="text-gray-600">All applications in the system</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={loadAllApplications} className="btn-outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Bifurcation Tabs */}
      {(() => {
        const counts = allAppsStageCounts || {};
        const totalCount = allAppsTotal || 0;
        const tabBtn = (id, label, count) => (
          <button
            key={id}
            onClick={() => { setLoading(true); setActiveAllAppsTab(id); setCurrentPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${activeAllAppsTab === id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
          >
            {label}
            <span className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs rounded-full ${activeAllAppsTab === id ? 'bg-white text-teal-700' : 'bg-gray-100 text-gray-700'}`}>
              {count}
            </span>
          </button>
        );
        return (
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="flex flex-wrap gap-2">
              {/* All Applications - Same buckets as Pending Applications */}
              {tabBtn('all', 'All', counts.all ?? totalCount)}
              {tabBtn('new', 'New Applications', counts['new'] || 0)}
              {tabBtn('query_raised', 'Query Raised', counts['query_raised'] || 0)}
              {tabBtn('kyp_query_reply_received', 'KYP Query Reply Received', counts['kyp_query_reply_received'] || 0)}
              {tabBtn('diagnosis_approval_pending', 'Diagnosis Approval Pending', counts['diagnosis_approval_pending'] || 0)}
              {tabBtn('kyp_pending', 'KYP Pending', counts['kyp_pending'] || 0)}
              {tabBtn('kyp_challenged', 'KYP Challenged', counts['kyp_challenged'] || 0)}
              {tabBtn('kyp_approved', 'KYP Approved', counts['kyp_approved'] || 0)}
              {tabBtn('discharge_pending', 'Discharge Pending', counts['discharge_pending'] || 0)}
              {tabBtn('discharge_query_reply_received', 'Discharge Query Reply Received', counts['discharge_query_reply_received'] || 0)}
              {tabBtn('discharge_approved', 'Discharge Approved', counts['discharge_approved'] || 0)}
              {tabBtn('fal_challenged', 'FAL Challenged', counts['fal_challenged'] || 0)}
              {tabBtn('fal_approved', 'FAL Approved', counts['fal_approved'] || 0)}
              {tabBtn('scrutiny_pending', 'Scrutiny Pending', counts['scrutiny_pending'] || 0)}
              {tabBtn('scrutiny_query_reply_received', 'Scrutiny Query Reply Received', counts['scrutiny_query_reply_received'] || 0)}
              {tabBtn('scrutiny_approved', 'Scrutiny Approved', counts['scrutiny_approved'] || 0)}
              {tabBtn('rejected_challenge', 'Rejected Challenged', counts['rejected_challenge'] || 0)}
              {tabBtn('rejected', 'Rejected', counts['rejected'] || 0)}
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-4">
          <div>
            <input
              type="text"
              placeholder="Search applications..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input-field"
            >
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="input-field"
            >
              {priorityOptions.map((priority) => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.emergency}
              onChange={(e) => handleFilterChange('emergency', e.target.value)}
              className="input-field"
            >
              <option value="">All Cases</option>
              <option value="true">Emergency Only</option>
              <option value="false">Non-Emergency</option>
            </select>
          </div>

          <div>
            <select
              value={filters.treatmentType}
              onChange={(e) => handleFilterChange('treatmentType', e.target.value)}
              className="input-field"
            >
              {treatmentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.insuranceCompany}
              onChange={(e) => handleFilterChange('insuranceCompany', e.target.value)}
              className="input-field"
            >
              <option value="">All Companies</option>
              {insuranceCompanies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button onClick={resetFilters} className="btn-outline text-sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset Filters
          </button>
          <div className="text-sm text-gray-600">
            {allAppsTotal} application(s) found
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-teal-500" />
            <span className="ml-2 text-gray-600">Loading applications...</span>
          </div>
        ) : allApplications.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Found</h3>
            <p className="text-gray-600">No applications match your current filters.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Application ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medical Information</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hospital & Doctor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Policy & Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lifecycle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allApplications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{application.applicationId}</div>
                        <div className="text-sm text-gray-500">
                          {application.workflow?.medicalReview?.status === 'in_progress' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                              <Timer className="h-3 w-3 mr-1" />
                              In Review
                            </span>
                          )}
                          {application.workflow?.medicalReview?.status === 'paused' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                              <Pause className="h-3 w-3 mr-1" />
                              Paused
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                            <User className="h-4 w-4 text-teal-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{application.patient?.name}</div>
                            <div className="text-sm text-gray-500">
                              {application.patient?.age}Y, {application.patient?.gender}
                            </div>
                            <div className="text-xs text-gray-400">
                              <Phone className="h-3 w-3 inline mr-1" />
                              {application.patient?.phone}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">{application.medical?.diagnosis}</div>
                          <div className="text-gray-500">
                            {application.medical?.treatmentType?.charAt(0).toUpperCase() +
                              application.medical?.treatmentType?.slice(1)} Treatment
                          </div>
                          <div className="text-xs text-gray-400">
                            Admission: {application.medical?.admissionDate}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center whitespace-normal break-words">
                            <Building className="h-3 w-3 mr-1 text-gray-400" />
                            {application.hospitalName}
                          </div>
                          <div className="text-gray-500">
                            Dr. {application.medical?.doctor?.name}
                          </div>
                          <div className="text-xs text-gray-400">
                            {application.medical?.doctor?.designation}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center">
                            <CreditCard className="h-3 w-3 mr-1 text-gray-400" />
                            {application.policy?.insuranceCompany}
                          </div>
                          <div className="text-gray-500 font-mono text-xs">
                            {application.policy?.number}
                          </div>
                          <div className="text-sm font-medium text-teal-600">
                            {formatCurrency(application.medical?.estimatedCost)}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(application.status)}`}>
                          {application.status?.charAt(0).toUpperCase() + application.status?.slice(1).replace('_', ' ')}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(application.priority)}`}>
                            {application.priority?.charAt(0).toUpperCase() + application.priority?.slice(1)}
                          </span>
                          {application.emergency && (
                            <div>
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                Emergency
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(application.submittedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderLifecycleChips(application)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center justify-center">
                          {application.workflow?.medicalReview?.status === 'paused' &&
                            application.workflow?.medicalReview?.reviewerId === user.uid ? (
                            // Resume button for paused applications by current doctor
                            <button
                              onClick={() => handleResumeReview(application.applicationId)}
                              disabled={actionLoading[application.applicationId]}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Resume Paused Review"
                            >
                              {actionLoading[application.applicationId] ? (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  Resuming...
                                </>
                              ) : (
                                <>
                                  <Play className="h-4 w-4 mr-2" />
                                  Resume
                                </>
                              )}
                            </button>
                          ) : application.workflow?.medicalReview?.status === 'in_progress' &&
                            application.workflow?.medicalReview?.reviewerId === user.uid ? (
                            // Continue button for applications being actively reviewed by current doctor
                            <button
                              onClick={() => handleViewApplication(application.applicationId)}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
                              title="Continue Review"
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Continue
                            </button>
                          ) : (application.workflow?.medicalReview?.status === 'in_progress' ||
                            application.workflow?.medicalReview?.status === 'paused') ? (
                            // View only for applications being reviewed/paused by others
                            <button
                              onClick={() => handleViewApplication(application.applicationId)}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              title="View (Under Review by Another Doctor)"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Only
                            </button>
                          ) : application.status === 'submitted' ? (
                            // Start button for new applications
                            <button
                              onClick={() => handleStartReview(application.applicationId)}
                              disabled={actionLoading[application.applicationId]}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Start Review"
                            >
                              {actionLoading[application.applicationId] ? (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  Starting...
                                </>
                              ) : (
                                <>
                                  <Play className="h-4 w-4 mr-2" />
                                  Start
                                </>
                              )}
                            </button>
                          ) : (
                            // View only for completed applications
                            <button
                              onClick={() => handleViewApplication(application.applicationId)}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="btn-outline text-sm"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="btn-outline text-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderReviewed = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Reviewed Applications</h1>
        <p className="text-gray-600">Applications you have completed medical review</p>
      </div>

      {/* Filters for reviewed applications */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <input
              type="text"
              placeholder="Search reviewed applications..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input-field"
            >
              <option value="">All Status</option>
              <option value="medical_approved">Approved</option>
              <option value="medical_rejected">Rejected</option>
              <option value="medical_pending_info">Pending Info</option>
            </select>
          </div>

          <div>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="input-field"
              placeholder="From Date"
            />
          </div>

          <div>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="input-field"
              placeholder="To Date"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button onClick={resetFilters} className="btn-outline text-sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset Filters
          </button>
          <div className="text-sm text-gray-600">
            {reviewedApplications.length} reviewed application(s)
          </div>
        </div>
      </div>

      {/* Reviewed Applications Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-teal-500" />
            <span className="ml-2 text-gray-600">Loading reviewed applications...</span>
          </div>
        ) : reviewedApplications.length === 0 ? (
          <div className="text-center py-12">
            <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Reviewed Applications</h3>
            <p className="text-gray-600">You haven't reviewed any applications yet.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Application</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Diagnosis</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reviewed On</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lifecycle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reviewedApplications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-normal break-words">
                        <div className="text-sm font-medium text-gray-900">{application.applicationId}</div>
                        <div className="text-sm text-gray-500">{application.hospitalName}</div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                            <User className="h-4 w-4 text-teal-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{application.patient?.name}</div>
                            <div className="text-sm text-gray-500">{application.patient?.age}Y, {application.patient?.gender}</div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{application.medical?.diagnosis}</div>
                        <div className="text-sm text-gray-500">{application.medical?.treatmentType}</div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${application.workflow?.medicalReview?.status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : application.workflow?.medicalReview?.status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {application.workflow?.medicalReview?.status === 'approved' ? 'Approved' :
                            application.workflow?.medicalReview?.status === 'rejected' ? 'Rejected' : 'Pending Info'}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {application.workflow?.medicalReview?.reviewTimeMinutes
                          ? `${application.workflow.medicalReview.reviewTimeMinutes} min`
                          : 'N/A'
                        }
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(application.workflow?.medicalReview?.reviewedAt)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderLifecycleChips(application)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewApplication(application.applicationId)}
                          className="text-teal-600 hover:text-teal-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination for reviewed applications */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="btn-outline text-sm"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="btn-outline text-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderProfile = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">My Profile</h1>
        <p className="text-gray-600">Manage your doctor profile and preferences</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
            <Stethoscope className="h-8 w-8 text-teal-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{user?.profile?.name || user?.email}</h3>
            <p className="text-gray-600">Medical Review Specialist</p>
            <p className="text-sm text-gray-500">{user?.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Contact Information</h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">{user?.profile?.phone || 'Not provided'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600">{user?.profile?.location || 'Not provided'}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Review Statistics</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Reviews:</span>
                <span className="text-sm font-medium text-gray-900">{stats.totalReviewed}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Approval Rate:</span>
                <span className="text-sm font-medium text-gray-900">{stats.approvalRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Avg Review Time:</span>
                <span className="text-sm font-medium text-gray-900">{stats.averageReviewTime} min</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <button className="btn-primary">
            Edit Profile
          </button>
        </div>
      </div>
    </div>
  );

  const renderApplicationsOnHold = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Applications on Hold</h1>
          <p className="text-gray-600">Applications with unresolved queries that are currently on hold</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={loadApplicationsOnHold} className="btn-outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-4">
          <div>
            <input
              type="text"
              placeholder="Search applications..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="input-field"
            >
              {priorityOptions.map((priority) => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.emergency}
              onChange={(e) => handleFilterChange('emergency', e.target.value)}
              className="input-field"
            >
              <option value="">All Cases</option>
              <option value="true">Emergency Only</option>
              <option value="false">Non-Emergency</option>
            </select>
          </div>

          <div>
            <select
              value={filters.treatmentType}
              onChange={(e) => handleFilterChange('treatmentType', e.target.value)}
              className="input-field"
            >
              {treatmentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.insuranceCompany}
              onChange={(e) => handleFilterChange('insuranceCompany', e.target.value)}
              className="input-field"
            >
              <option value="">All Companies</option>
              {insuranceCompanies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={filters.hospitalName}
              onChange={(e) => handleFilterChange('hospitalName', e.target.value)}
              className="input-field"
            >
              <option value="">All Hospitals</option>
              {/* Add hospital options dynamically */}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button onClick={resetFilters} className="btn-outline text-sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset Filters
          </button>
          <div className="text-sm text-gray-600">
            {onHoldApplications.length} application(s) on hold
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-teal-500" />
            <span className="ml-2 text-gray-600">Loading applications...</span>
          </div>
        ) : onHoldApplications.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications on Hold</h3>
            <p className="text-gray-600">All applications are clear to proceed with no unresolved queries.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Application ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medical Information</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hospital & Doctor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Query Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {onHoldApplications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">
                            {application.applicationId}
                          </div>
                          {application.emergency && (
                            <span className="ml-2 px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                              Emergency
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(application.submittedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {application.patient?.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {application.patient?.age} years, {application.patient?.gender}
                        </div>
                        <div className="text-xs text-gray-500">
                          {application.patient?.phone}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {application.medical?.diagnosis}
                        </div>
                        <div className="text-sm text-gray-500">
                          {application.medical?.treatmentType}
                        </div>
                        <div className="text-xs text-gray-500">
                          ₹{application.medical?.estimatedCost?.toLocaleString('en-IN')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-normal break-words">
                        <div className="text-sm font-medium text-gray-900">
                          {application.hospitalName}
                        </div>
                        <div className="text-sm text-gray-500">
                          Dr. {application.medical?.doctor?.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="h-4 w-4 text-orange-500" />
                          <div>
                            <div className="text-sm font-medium text-orange-800">
                              {application.queryStatus?.queryCount || 0} Unresolved Queries
                            </div>
                            {application.queryStatus?.unresolvedQueries?.map((query, index) => (
                              <div key={index} className="text-xs text-gray-600">
                                {query.title || 'Query pending'}
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
                          On Hold - Queries Pending
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={(e) => {
                            if (e.ctrlKey || e.metaKey) {
                              e.preventDefault();
                              e.stopPropagation();
                              const url = `${window.location.origin}/doctor/review/${application.applicationId}`;
                              window.open(url, '_blank', 'noopener,noreferrer');
                              return;
                            }
                            navigate(`/doctor/review/${application.applicationId}`);
                          }}
                          className="btn-outline text-sm"
                          title="View Application Details"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="btn-outline text-sm disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="btn-outline text-sm disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="text-sm text-gray-500">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, onHoldApplications.length)} of {onHoldApplications.length} results
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboard();
      case 'applications':
        return renderApplications();
      case 'on-hold':
        return renderApplicationsOnHold();
      case 'all-applications':
        return renderAllApplications();
      case 'reviewed':
        return renderReviewed();
      case 'profile':
        return renderProfile();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-teal-100 fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center px-6 py-4">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="flex items-center space-x-2">
              <img src={samhealthLogo} alt="SamHealth" className="h-8 w-auto object-contain" />
              <p className="text-sm text-teal-600">Doctor Portal</p>
            </div>
          </div>

          <div className="flex items-center space-x-4 flex-1 justify-end">
            {/* Universal Search Bar */}
            <div className="w-64 hidden md:block">
              <UniversalSearch userRole="doctor" />
            </div>

            <div className="flex items-center space-x-4 flex-shrink-0">
            <NotificationBell />
            <div className="text-right">
              <p className="text-sm font-medium text-gray-800">Dr. {user?.profile?.name || 'Doctor'}</p>
              <p className="text-xs text-gray-600">{user?.email}</p>
            </div>
            <RoleSwitcher buttonClassName="border-gray-300 hover:border-teal-400 hover:text-teal-700" />
            {/* Removed decorative header icon */}
            <SignOutIconButton
              size="sm"
              showConfirmModal={true}
              confirmMode="modal"
              className="hover:bg-red-100"
            />
            </div>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside className="w-64 shrink-0 bg-white/95 backdrop-blur-sm shadow-[0_0_0_1px_rgba(0,0,0,0.04),0_10px_30px_-12px_rgba(0,0,0,0.25)] border-r border-gray-200 fixed lg:sticky top-16 lg:top-16 left-0 h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] z-30 overflow-y-auto will-change-transform relative">
          <nav className="mt-8">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.id === 'documents') {
                      navigate('/documents');
                    } else {
                      setActiveSection(item.id);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-6 py-3 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50 ${
                    isActive
                      ? "bg-teal-50 text-teal-700 border-r-2 border-teal-500"
                      : "text-gray-800 hover:bg-gray-50 hover:text-teal-700"
                  }`}
                >
                  <span className="flex items-center">
                    <Icon className="h-5 w-5 mr-3" />
                    {item.label}
                  </span>
                  {item.id === 'applications' && stats.pendingReview > 0 && (
                    <span className="ml-3 bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {stats.pendingReview}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
          {/* Subtle right-edge gradient to separate content */}
          <div className="pointer-events-none absolute top-0 right-0 h-full w-4 bg-gradient-to-r from-transparent to-gray-200/40"></div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:ml-0 ml-64">{renderContent()}</main>
      </div>
    </div>
  );
};

export default DoctorDashboard;
